import React, { useEffect, useState, useTransition } from "react";

import { faArrowRight } from "@fortawesome/pro-regular-svg-icons";
import { twMerge } from "tailwind-merge";

import { Card } from "src/base-components/Card";
import { Callout } from "src/design-system/Callout";
import { Icon } from "src/design-system/Icon";
import { Skeleton } from "src/design-system/Skeleton";
import { SchemaError } from "src/parentFlowNodes/flowNode/types";
import { MissingSchema } from "src/parentFlowNodes/MissingSchema";
import {
  SCHEMA_MAPPING_ARROW_WIDTH,
  SchemaProperty,
} from "src/parentFlowNodes/SchemaProperty";
import { buildSchemaTabUrl } from "src/parentFlowNodes/utils";
import { useAuthoringContext } from "src/router/routerContextHooks";
import { SchemaOptions } from "src/router/SearchParams";
import { SchemaUI } from "src/schema/schemaMappingUtils";

type Props = {
  title: string;
  description?: React.ReactNode;
  childFlowName: string;
  schema?: SchemaUI;
  isOutput?: boolean;
  childFlowId: string;
  childFlowSlug: string;
  childVersionId: string;
  isNodeStale: boolean;
  readonly?: boolean;
  hasError?: boolean;
  fieldMappingErrors: Record<string, SchemaError>;
  isLoopNode?: boolean;
  highlightedFields: string[];
  lazyInputs: boolean;
};

const SkeletonRow = () => (
  <div className="flex items-center gap-x-3">
    <div className="w-1/2 min-w-0 rounded-lg">
      <Skeleton height="h-8" />
    </div>
    <div className={SCHEMA_MAPPING_ARROW_WIDTH}>
      <Icon icon={faArrowRight} size="xs" />
    </div>
    <div className="w-1/2 min-w-0 rounded-lg">
      <Skeleton height="h-8" />
    </div>
  </div>
);

export const DataMap: React.FC<Props> = ({
  lazyInputs,
  schema,
  title,
  description,
  childFlowName,
  childFlowId,
  childVersionId,
  isOutput = false,
  readonly = false,
  hasError = false,
  fieldMappingErrors,
  isNodeStale,
  isLoopNode = false,
  highlightedFields,
}) => {
  const { orgId, workspace } = useAuthoringContext();
  const [displayedCount, setDisplayedCount] = useState(0);
  const [, startTransition] = useTransition();

  useEffect(() => {
    // checking if we have already displayed all the properties
    if (
      !schema?.properties.length ||
      displayedCount >= schema.properties.length
    ) {
      return;
    }

    startTransition(() => {
      // adding 20 properties at a time, which are rendered in background
      // avoids frozen UI when there are too many properties to render
      setDisplayedCount((prev) => prev + 20);
    });
  }, [startTransition, displayedCount, schema?.properties.length]);

  return (
    <div className="flex flex-col gap-y-3">
      {hasError && isNodeStale && (
        <Callout variant="warning">
          Map required fields to complete configuration
        </Callout>
      )}
      <span className="font-inter-semibold-13px text-gray-800">{title}</span>
      {description && (
        <p className="font-inter-normal-12px text-gray-500">{description}</p>
      )}
      <Card variant={schema ? "default" : "inverted"}>
        <Card.Content>
          <div className="flex flex-col gap-y-2">
            {!schema ? (
              <MissingSchema
                childFlowId={childFlowId}
                childVersionId={childVersionId}
                flowName={childFlowName}
                isOutput={isOutput}
                warn={isNodeStale}
              />
            ) : (
              <>
                <div
                  className={twMerge(
                    "mb-1 flex w-full gap-x-3",
                    isOutput && "flex-row-reverse",
                  )}
                >
                  <p className="w-1/2 font-inter-normal-12px text-gray-500">
                    Current Decision Flow
                  </p>
                  <div className={SCHEMA_MAPPING_ARROW_WIDTH} />
                  <p className="w-1/2 font-inter-normal-12px text-gray-500">
                    {childFlowName}
                  </p>
                </div>
                {schema.properties.map((field, index) => {
                  if (index > displayedCount) {
                    return <SkeletonRow key={field.fieldName} />;
                  }

                  return (
                    <SchemaProperty
                      key={field.fieldName}
                      field={field}
                      fieldError={!!fieldMappingErrors[field.fieldName]}
                      highlighted={highlightedFields.includes(field.fieldName)}
                      isLoopNode={isLoopNode}
                      isOutput={isOutput}
                      lazy={lazyInputs}
                      readonly={readonly}
                      schemaTabUrl={buildSchemaTabUrl(
                        orgId,
                        workspace.id,
                        childFlowId,
                        childVersionId,
                        isOutput ? SchemaOptions.Output : SchemaOptions.Input,
                      )}
                      shouldDisplayError={isNodeStale}
                    />
                  );
                })}
              </>
            )}
          </div>
        </Card.Content>
      </Card>
    </div>
  );
};
